import React, { useState, useEffect, useContext } from 'react'
import {
  Breadcrumb,
  Button,
  Card,
  Form,
  Container,
  Spinner,
} from 'react-bootstrap'
import { Formik } from 'formik'
import { Navigate } from 'react-router-dom'

import { UserContext } from '../context'
import { getUser, getAccessToken } from '../services/AuthService'
import axios from 'axios'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import SubscribeBanner from './SubscribeBanner'
import PaymentMethodsList from './PaymentMethodsList'
import { showErrorNotification } from '../services/notificationService'
import { set } from 'date-fns'

// Main component for user account management
function UserAccount({ isLoggedIn,  logOut }) {
  const [state, , actions] = useContext(UserContext);
  // Local state for user data and form control
  const [userData, setUserData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password1: '',
    password2: '',
    telephone: '',
  })
  const [isFormEnabled, setIsFormEnabled] = useState(false)
  const [isSubmitted, setSubmitted] = useState(false)

  // Fetch user data on component mount
  useEffect(() => {
    if (state.ready && state.auth && !state.user) {
      actions.refreshUser(); // one-shot; provider already sets axios header
    }
  }, [state.ready, state.auth, state.user, actions]);

  // Enable form for editing
  const handleButtonClick = () => {
    setIsFormEnabled(true)
  }

  // Copy user into local form state once it exists
  useEffect(() => {
    if (state.user) setUserData(prev => ({ ...prev, ...state.user }));
  }, [state.user?.id]); // change only when a real user arrives

  // Update local user data state on form change
  const handleChange = (e) => {
    const { name, value } = e.target
    setUserData((prevValues) => ({
      ...prevValues,
      [name]: value ?? '',
    }))
  }

  const handleSubmit = async (values, formikHelpers) => {
    const { setSubmitting } = formikHelpers;

    if (!state?.user?.id) {
      toast.error('Usuario no disponible.');
      return;
    }

    const url = `${process.env.REACT_APP_BASE_URL}/api/users/${state.user.id}/`;
    const headers = { Authorization: `Bearer ${state.auth}` };

    // Build a diff against the last loaded user values (userData is your baseline)
    const diff = {};
    for (const k of Object.keys(values)) {
      const next = values[k] ?? '';
      const prev = userData?.[k] ?? '';
      if (next !== prev) diff[k] = next;
    }

    // If no changes, bail early
    if (Object.keys(diff).length === 0) {
      toast.info('No hay cambios por guardar');
      return;
    }

    // Only send passwords if the user actually filled them
    if (!values.password1 && !values.password2) {
      delete diff.password1;
      delete diff.password2;
    }

    // Optimistic update (exclude passwords)
    const { password1, password2, ...optimisticPatch } = diff;
    const previous = state.user;
    actions.updateUserLocally(prev => ({ ...prev, ...optimisticPatch }));

    try {
      const { data } = await axios.patch(url, diff, { headers, timeout: 8000 });

      // Prefer canonical data from server if it’s returned,
      // otherwise refetch to avoid drift.
      if (data && typeof data === 'object') {
        actions.updateUserLocally(data);
      } else {
        await actions.refreshUser();
      }

      // Refresh local form baseline so future diffs are correct
      setUserData(ud => ({ ...ud, ...optimisticPatch }));

      setSubmitted(true);            // keep your existing redirect behavior
      setIsFormEnabled(false);
      toast.success('Tu usuario se actualizó');
    } catch (err) {
      // Roll back optimistic update on failure
      actions.updateUserLocally(previous);
      showErrorNotification(err);
    } finally {
      setSubmitting(false);
    }
  };
  // Yup validation schema
  const validationSchema = Yup.object().shape({
    username: Yup.string().required('Username es requerido'),
    email: Yup.string()
      .email('Correo invalido')
      .required('Correo es requerido'),
    first_name: Yup.string().required('Primer nombre es requerido'),
    last_name: Yup.string().required('Apellidos requeridos'),
    password1: Yup.string()
      .required('Password es requerida')
      .min(8, 'Password must be at least 8 characters long'),
    password2: Yup.string().oneOf(
      [Yup.ref('password1'), null],
      'Passwords no coinciden'
    ),
    telephone: Yup.string()
      .required('Telefono es requerido')
      .matches(/^[0-9]+$/, 'Telefono tiene que ser numerico'),
  })

  
  if (!state.ready) {
    return (
      <Container className="mt-2">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading…</span>
        </Spinner>
      </Container>
    );
  }

  const bootstrapingUser =  state.auth && !state.user;
  if (bootstrapingUser) {
    return (
      <Container className="mt-2">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading…</span>
        </Spinner>
      </Container>
    );
  }
  if (state.ready && !state.auth) {
    return <Navigate to="/" replace />;
  }

  if (isSubmitted) {
    return <Navigate to="/" replace />;
  }
  // Render user account form
  return (
    <Container className='mt-2 user_info_container'>
      <Container>
        {state.user && state.user.active ? (
          <UserSubscription logOut={logOut} />
        ) : (
          <SubscribeBanner />
        )}
      </Container>

      <Container>
        {state.user && state.user.paypal_subscription_id ? (
          <></>
        ) : (
          <PaymentMethodsList isLoggedIn={isLoggedIn} />
        )}
      </Container>

      <Container className='mt-2  user_form'>
        <Formik
          initialValues={userData || { username: '', email: '', first_name: '', last_name: '', password1: '', password2: '', telephone: '' }}
          enableReinitialize={true}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ handleSubmit, handleChange, values, errors, isSubmitting }) => (
            <Form onSubmit={handleSubmit}>
              <Form.Group className='mb-3' controlId='username'>
                <Form.Label>Usuario:</Form.Label>
                <Form.Control
                  className={'username' in errors ? 'is-invalid' : ''}
                  name='username'
                  onChange={handleChange}
                  required
                  value={values.username ? values.username : ''}
                  disabled={!isFormEnabled}
                />
                {'username' in errors && (
                  <Form.Control.Feedback type='invalid'>
                    {errors.username}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <Form.Group className='mb-3' controlId='email'>
                <Form.Label>Email:</Form.Label>
                <Form.Control
                  className={'email' in errors ? 'is-invalid' : ''}
                  type='email'
                  name='email'
                  onChange={handleChange}
                  required
                  value={values.email ? values.email : ''}
                  disabled={!isFormEnabled}
                />
                {'email' in errors && (
                  <Form.Control.Feedback type='invalid'>
                    {errors.email}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Form.Group className='mb-3' controlId='first_name'>
                <Form.Label>Nombre:</Form.Label>
                <Form.Control
                  className={'first_name' in errors ? 'is-invalid' : ''}
                  name='first_name'
                  onChange={handleChange}
                  required
                  value={values.first_name ? values.first_name : ''}
                  disabled={!isFormEnabled}
                />
                {'first_name' in errors && (
                  <Form.Control.Feedback type='invalid'>
                    {errors.first_name}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Form.Group className='mb-3' controlId='last_name'>
                <Form.Label>Apellidos:</Form.Label>
                <Form.Control
                  className={'last_name' in errors ? 'is-invalid' : ''}
                  name='last_name'
                  onChange={handleChange}
                  required
                  value={values.last_name ? values.last_name : ''}
                  disabled={!isFormEnabled}
                />

                {'last_name' in errors && (
                  <Form.Control.Feedback type='invalid'>
                    {errors.last_name}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Form.Group className='mb-3' controlId='password1'>
                <Form.Label>Contraseña:</Form.Label>
                <Form.Control
                  className={'password1' in errors ? 'is-invalid' : ''}
                  name='password1'
                  onChange={handleChange}
                  required
                  type='password'
                  value={values.password1 ? values.password1 : ''}
                  disabled={!isFormEnabled}
                />
                {'password1' in errors && (
                  <Form.Control.Feedback type='invalid'>
                    {errors.password1}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <Form.Group className='mb-3' controlId='password2'>
                <Form.Label>Confirma Contraseña:</Form.Label>
                <Form.Control
                  className={'password2' in errors ? 'is-invalid' : ''}
                  name='password2'
                  onChange={handleChange}
                  required
                  type='password'
                  value={values.password2 ? values.password2 : ''}
                  disabled={!isFormEnabled}
                />
                {'password2' in errors && (
                  <Form.Control.Feedback type='invalid'>
                    {errors.password2}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <Form.Group className='mb-3' controlId='telephone'>
                <Form.Label>Teléfono:</Form.Label>
                <Form.Control
                  className={'telephone' in errors ? 'is-invalid' : ''}
                  name='telephone'
                  type='tel'
                  onChange={handleChange}
                  required
                  value={values.telephone ? values.telephone : ''}
                  disabled={!isFormEnabled}
                />
                {'telephone' in errors && (
                  <Form.Control.Feedback type='invalid'>
                    {errors.telephone}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <Button variant='primary' onClick={handleButtonClick}>
                Habilitar formulario
              </Button>

              <Button variant='primary' type='submit' disabled={!isFormEnabled}>
                Enviar
              </Button>
            </Form>
          )}
        </Formik>
      </Container>
    </Container>
  )
}

// Component to display user subscription details
function UserSubscription({ logOut }) {
  const [state] = useContext(UserContext);
  const user = state?.user;

  const [userSub, setUserSub] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshSubData, setRefreshSubData] = useState(false);


  useEffect(() => {
    const getSub = async () => {
      if (!user?.id) { setIsLoading(false); return; }

      setIsLoading(true);
      const isPaypal = !!user.paypal_subscription_id || user.subscription_type === 'paypal';
      const base = process.env.REACT_APP_BASE_URL;
      const url = isPaypal
        ? `${base}/api/subscription_plan_paypal/${user.id}/`
        : `${base}/api/create_subscription/${user.id}/`;

      try {
        const headers = { Authorization: `Bearer ${state.auth}` };
        const { data } = await axios.get(url, { headers, timeout: 5000 });
        console.log('Fetched subscription data:', data);

        const normalized = isPaypal ? normalizePaypalSub(data)
          : normalizeStripeSub(data);
        setUserSub(normalized);
      } catch (err) {
        console.error('Error getting subscription:', err?.response?.data || err?.message);
        logOut?.();
      } finally {
        setIsLoading(false);
      }
    };

    getSub();
  }, [user?.id, user?.paypal_subscription_id, user?.subscription_type, state.auth, refreshSubData]);
  // --- handlers (updated) ----------------------------------------------------
  const handleCancelSubscription = async () => {
    if (!user?.id) {
      toast.error('No se encontró el usuario.');
      return;
    }

    const isPaypal =
      !!user?.paypal_subscription_id || userSub?.subscription_type === 'paypal';

    const base = process.env.REACT_APP_BASE_URL;
    // For actions (pause/resume) your API expects USER id in the URL
    const url = isPaypal
      ? `${base}/api/subscription_plan_paypal/${user.id}/`
      : `${base}/api/create_subscription/${user.id}/`;

    try {
      const token = getAccessToken();
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(url, { headers, timeout: 7000 });
      toast.success('Tu subscripción fue pausada.');
      setRefreshSubData((p) => !p);
    } catch (error) {
      toast.error('Error al intentar cancelar tu subscripción, por favor contáctanos.');
      console.error('Error deleting subscription:', error?.response?.data || error?.message);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!user?.id) {
      toast.error('No se encontró el usuario.');
      return;
    }

    const isPaypal =
      !!user?.paypal_subscription_id || userSub?.subscription_type === 'paypal';

    const base = process.env.REACT_APP_BASE_URL;
    const url = isPaypal
      ? `${base}/api/subscription_plan_paypal/${user.id}/`
      : `${base}/api/create_subscription/${user.id}/`;

    try {
      const token = getAccessToken();
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(url, {}, { headers, timeout: 7000 });
      toast.success('Tu subscripción fue reactivada.');
      setRefreshSubData((p) => !p);
    } catch (error) {
      toast.error('Error al intentar reactivar tu subscripción, por favor contáctanos.');
      console.error('Error reactivate subscription:', error?.response?.data || error?.message);
    }
  };
  // --------------------------------------------------------------------------

  const formatMXN = (cents) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })
      .format((cents || 0) / 100);

  if (isLoading) {
    return (
      <div className="text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  // If there is no subscription, render nothing here (SubscribeBanner is shown by parent)
  if (!userSub) return null;

  console.log('User subscription data:', userSub);

  const cancelled = !!userSub.cancel_at_period_end;

  return (
    <Card>
      <Card.Body>
        <Card.Title>Subscripción:</Card.Title>
        <Card.Text>
          <strong>Plan:</strong> {userSub.product_name || '—'}
          <br />
          <strong>Precio: </strong>
          {userSub.subscription_type === 'paypal'
            ? (userSub.product_price ? `\$${userSub.product_price} MXN` : '—')
            : formatMXN(userSub.product_price)}
          <br />
          {!cancelled ? (
            <strong>Siguiente cobró:  </strong>
          ) : (
            <strong>Cuenta activa hasta : </strong>
          )}
          <TimestampToDate timestamp={userSub.current_period_end} />
          <br />
          <strong>Status: </strong>
          {userSub.status}
        </Card.Text>

        {!cancelled ? (
          <Button variant="danger" onClick={handleCancelSubscription}>
            Cancel Subscription
          </Button>
        ) : (
          <Button variant="primary" onClick={handleReactivateSubscription}>
            Reactiva subscripción
          </Button>
        )}
      </Card.Body>
    </Card>
  );
}

// Normalize helpers ----------------------

function normalizePaypalSub(data) {
  console.log('Raw PayPal subscription data:', data);
  const next =
    data?.billing_info?.next_billing_time ||
    data?.billing_info?.cycle_executions?.[0]?.next_billing_time ||
    null;

  const planName = data?.plan?.name || data?.plan_name || '';
  const unitPrice =
    data?.plan?.billing_cycles?.[0]?.pricing_scheme?.fixed_price?.value ??
    data?.product_price ?? null;

  return {
    subscription_type: 'paypal',
    status: data?.status || '',
    product_name: data?.product_name,
    product_price: data?.product_price,               // "499.00" string or number
    current_period_end: data?.current_period_end,               // ISO string (or null)
    cancel_at_period_end: ['CANCELLED', 'SUSPENDED'].includes(data?.status),
  };
}


function normalizeStripeSub(data) {
  return {
    subscription_type: 'stripe',
    status: data?.status,
    product_name: data?.product_name,
    product_price: data?.product_price,
    current_period_end: data?.current_period_end,
    cancel_at_period_end: !!data?.cancel_at_period_end,
  };
}


// Component to convert timestamp to readable date
function TimestampToDate({ timestamp, fallback = '—' }) {
  if (timestamp == null) return <>{fallback}</>;

  let ms;
  if (typeof timestamp === 'number') {
    // handle seconds vs milliseconds
    ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
  } else if (typeof timestamp === 'string') {
    const parsed = Date.parse(timestamp);
    if (Number.isNaN(parsed)) return <>{fallback}</>;
    ms = parsed;
  } else {
    return <>{fallback}</>;
  }

  const date = new Date(ms);
  if (isNaN(date.getTime())) return <>{fallback}</>;

  const formatted = date.toLocaleString('es-ES', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });

  return <>{formatted}</>;
}


export default UserAccount
